import React from 'react'
import { castDraft } from 'immer'
import type { StateCreator } from 'zustand'
import type { WritableDraft } from 'immer'
import { Message } from 'rsuite'
import type { ArtifactReport } from '../../../../../api/generated/models/artifactReport'
import type { ScenarioReport } from '../../../../../api/generated/models/scenarioReport'
import type { ReportDocument } from '../../../../../api/generated/models/reportDocument'

// Union type for AnyReport (combining ArtifactReport and ScenarioReport)
export type AnyReport = ArtifactReport | ScenarioReport 

// Utility function to convert string numeric values to numbers for backwards compatibility
const convertStringNumbersToNumbers = (report: AnyReport): AnyReport => {
  if (!report) return report

  const convertedReport = { ...report } as any

  // Convert specific fields that should be numbers but might be strings in old data
  if (typeof convertedReport.total_observations === 'string') {
    convertedReport.total_observations =
      parseInt(convertedReport.total_observations, 10) || 0
  }

  if (typeof convertedReport.model_fit_score === 'string') {
    convertedReport.model_fit_score =
      parseFloat(convertedReport.model_fit_score) || 0
  }

  if (typeof convertedReport.model_health_score === 'string') {
    convertedReport.model_health_score =
      parseFloat(convertedReport.model_health_score) || 0
  }

  // Handle included_observations if it exists and is a string
  if (
    'included_observations' in convertedReport &&
    typeof convertedReport.included_observations === 'string'
  ) {
    convertedReport.included_observations =
      parseInt(convertedReport.included_observations, 10) || 0
  }

  return convertedReport as AnyReport
}

// Enum for different AI text types
export enum AITextType {
  PLOT_DESCRIPTION = 'PLOT_DESCRIPTION',
  PLOT_DESCRIPTION_PLUS = 'PLOT_DESCRIPTION_PLUS',
  CONCLUSION = 'CONCLUSION',
  CONCLUSION_PLUS = 'CONCLUSION_PLUS',
  KEY_INSIGHTS = 'KEY_INSIGHTS',
  KEY_INSIGHTS_PLUS = 'KEY_INSIGHTS_PLUS',
}

// Export status interface - moved from derived stores
export interface ExportStatus {
  show: boolean
  success: boolean
  opacity: number
}

// Export result interface for consistent return types
export interface ExportResult {
  success: boolean
  blob?: Blob
  filename?: string
  error?: string
}

// ---- BaseSlice (common) ----
export type BaseSlice = {
  anyReport: AnyReport | null

  // Change tracking
  hasUnsavedChanges: boolean

  // Common report state - moved from derived stores
  exportStatus: ExportStatus
  fetchingReportIsLoading: boolean
  freshReportId: number | null
  routedReportId: number | undefined

  // User context
  userName: string | null

  // Actions
  setTitle: (title: string) => void
  initialize: (anyReport?: AnyReport | null) => void // Initialize both title and any report without tracking changes
  resetCommonStoreData: () => void

  setAIText: (params: {
    plotGroupId: number
    plotPackageId: number
    text: string | null
    hasPlus: boolean
  }) => void
  setReportLevelAIText: (params: {
    textSrcType: AITextType
    text: string | null
  }) => void

  // Change tracking actions
  setHasUnsavedChanges: (hasChanges: boolean) => void

  // User context actions
  setUserName: (userName: string) => void

  // Export utilities - extracted from derived stores
  processDownloadStream: (
    response: Response,
    onProgress?: (progress: number) => void,
  ) => Promise<Blob>
  triggerFileDownload: (blob: Blob, filename: string, pushToast: any) => void
  animateExportStatus: (hasError: boolean) => void
  baseHandleExport: (
    formats: string[],
    params: {
      pushToast: any
      setProgress: (progress: number) => void
      exportReportData: (
        format: string,
        onProgress?: (progress: number) => void,
      ) => Promise<ExportResult>
    },
  ) => Promise<void>

  // Report fetching utilities
  fetchReportFromServer: (
    endpoint: string,
    reportId: string,
    pushToast?: any,
  ) => Promise<void>

  // Save report template - extracted common save logic
  handleSaveReportTemplate: (params: {
    savingStateKey?: string
    updateFunction: (reportId: number) => Promise<boolean>
    saveNewFunction: () => Promise<boolean>
    pushToast: any
    setSavingState: (key: string | null, value: boolean) => void
  }) => Promise<void>

  // Getter functions for AI-related properties
  getTitle: () => string | null
  getAIPrompts: () => any
  getAIPlus: () => boolean
  getAISettings: () => any | undefined
  getReportDocument: () => ReportDocument | undefined
  getUserName: () => string // Moved from derived stores
}

export const createBaseSlice: StateCreator<
  BaseSlice,
  [['zustand/immer', never]],
  [],
  BaseSlice
> = (set, get) => ({
  anyReport: null,
  hasUnsavedChanges: false,

  // Common report state - initialized
  exportStatus: { show: false, success: true, opacity: 0 },
  fetchingReportIsLoading: false,
  freshReportId: null,
  routedReportId: undefined,

  // User context - initialized
  userName: null,

  // Actions
  setTitle: (title: string) => {
    set((state) => {
      if (state.anyReport) {
        state.anyReport.name = title
      }
    })
    get().setHasUnsavedChanges(true)
  },

  initialize: (anyReport?: AnyReport | null) =>
    set((state) => {
      if (anyReport !== undefined) {
        // Apply backwards compatibility conversion for string numbers only if report is not null
        const convertedReport = anyReport
          ? convertStringNumbersToNumbers(anyReport)
          : null
        state.anyReport = castDraft(convertedReport)
        // If title is provided after anyReport is set, update the name
      }

      // Don't track changes for initial load
    }),

  resetCommonStoreData: () => {
    set((state) => {
      state.anyReport = null
      state.routedReportId = undefined
      state.exportStatus = { show: false, success: true, opacity: 0 }
      state.fetchingReportIsLoading = false
      state.freshReportId = null
    })
    get().setHasUnsavedChanges(false)
  },

  setAIText: (params: {
    plotGroupId: number
    plotPackageId: number
    text: string | null
    hasPlus: boolean
  }) => {
    set((state) => {
      if (!state.anyReport?.executive_summary?.plots) return

      // Find the plot group by ID
      const plotGroupIndex = state.anyReport.executive_summary.plots.findIndex(
        (group: any) => group.id === params.plotGroupId,
      )

      if (
        plotGroupIndex === -1 ||
        plotGroupIndex >= state.anyReport.executive_summary.plots.length
      ) {
        console.warn('PlotGroup not found')
        return
      }

      const plotGroup = state.anyReport.executive_summary.plots[plotGroupIndex]
      if (!plotGroup.plots) return

      // Find the individual plot by ID
      const plotPackageIndex = plotGroup.plots.findIndex(
        (plot: any) => plot.id === params.plotPackageId,
      )

      if (
        plotPackageIndex === -1 ||
        plotPackageIndex >= plotGroup.plots.length
      ) {
        console.warn('PlotPackage not found')
        return
      }

      // Update the appropriate AI description based on hasPlus flag
      const plotPackage = plotGroup.plots[plotPackageIndex]
      if (params.hasPlus) {
        plotPackage.ai_plus_description = params.text
      } else {
        plotPackage.ai_description = params.text
      }
    })

    // Track changes
    get().setHasUnsavedChanges(true)
  },

  setReportLevelAIText: (params: {
    textSrcType: AITextType
    text: string | null
  }) => {
    set((state) => {
      if (!state.anyReport?.executive_summary) return

      switch (params.textSrcType) {
        case AITextType.CONCLUSION:
          state.anyReport.executive_summary.conclusion = params.text
          break
        case AITextType.CONCLUSION_PLUS:
          state.anyReport.executive_summary.conclusion_plus = params.text
          break
        case AITextType.KEY_INSIGHTS:
          state.anyReport.executive_summary.key_insights = params.text
          break
        case AITextType.KEY_INSIGHTS_PLUS:
          state.anyReport.executive_summary.key_insights_plus = params.text
          break
        default:
          console.warn('Invalid textType for report level AI text')
      }
    })

    // Track changes
    get().setHasUnsavedChanges(true)
  },

  // Change tracking actions
  setHasUnsavedChanges: (hasChanges: boolean) =>
    set((state) => {
      state.hasUnsavedChanges = hasChanges
    }),

  // User context actions
  setUserName: (userName: string) =>
    set((state) => {
      state.userName = userName
    }),

  // Export utilities - extracted from derived stores
  processDownloadStream: async (
    response: Response,
    onProgress?: (progress: number) => void,
  ) => {
    // Check for missing content length header
    const contentLength = response.headers.get('Content-Length')
    if (!contentLength && onProgress) {
      console.warn(
        'Content-Length header is missing - progress tracking may be inaccurate',
      )
    }

    // If we have progress tracking, process the stream
    if (onProgress && response.body) {
      const reader = response.body.getReader()
      const totalSize = contentLength ? parseInt(contentLength, 10) : 0
      let receivedSize = 0
      const chunks = []

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        chunks.push(value)
        receivedSize += value.length

        // Update progress if we know the total size
        if (totalSize && onProgress) {
          const progress = Math.round((receivedSize / totalSize) * 100)
          onProgress(progress)
        }
      }

      return new Blob(chunks)
    } else {
      // Fallback to simple blob conversion without progress tracking
      return await response.blob()
    }
  },

  triggerFileDownload: (blob: Blob, filename: string, pushToast: any) => {
    try {
      // Create and trigger download of the file
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)

      pushToast(
        React.createElement(
          Message,
          { type: 'success' },
          `${filename} downloaded successfully.`,
        ),
        { placement: 'topCenter', duration: 5000 },
      )
    } catch (error) {
      pushToast(
        React.createElement(
          Message,
          { type: 'error' },
          `Failed to download file: ${(error as Error).message}`,
        ),
        { placement: 'topCenter', duration: 5000 },
      )
    }
  },

  animateExportStatus: (hasError: boolean) => {
    // Reset progress and show status message with animation
    set((state: WritableDraft<BaseSlice>) => {
      state.exportStatus = { show: true, success: !hasError, opacity: 0 }
    })

    // Animate status message
    setTimeout(() => {
      set((state: WritableDraft<BaseSlice>) => {
        state.exportStatus.opacity = 1
      })
    }, 50)

    setTimeout(() => {
      set((state: WritableDraft<BaseSlice>) => {
        state.exportStatus.opacity = 0
      })
      setTimeout(() => {
        set((state: WritableDraft<BaseSlice>) => {
          state.exportStatus.show = false
        })
      }, 1000)
    }, 5000)
  },

  baseHandleExport: async (
    formats: string[],
    params: {
      pushToast: any
      setProgress: (progress: number) => void
      exportReportData: (
        format: string,
        onProgress?: (progress: number) => void,
      ) => Promise<ExportResult>
    },
  ) => {
    const { pushToast, setProgress, exportReportData } = params
    const state = get()

    setTimeout(() => setProgress(5), 0)
    const totalFormats = formats.length
    let overallProgress = 0
    let hasError = false

    // Process each selected format
    for (let i = 0; i < totalFormats; i++) {
      const format = formats[i]

      // Set initial progress for this format
      const baseProgress = Math.round((overallProgress / totalFormats) * 100)
      setProgress(baseProgress)

      try {
        // Create progress callback for this specific format
        const onFormatProgress = (formatProgress: number) => {
          // Calculate combined progress: base progress + format progress within this format's slice
          const formatSliceSize = 100 / totalFormats
          const combinedProgress =
            baseProgress + Math.round((formatProgress / 100) * formatSliceSize)
          setProgress(Math.min(combinedProgress, 100))
        }

        const result = await exportReportData(format, onFormatProgress)

        if (result.success && result.blob && result.filename) {
          state.triggerFileDownload(result.blob, result.filename, pushToast)
        } else {
          hasError = true
          pushToast(
            React.createElement(
              Message,
              { type: 'error' },
              `Failed to download: ${result.error || 'Unknown error'}`,
            ),
            { placement: 'topCenter', duration: 5000 },
          )
        }
      } catch (error) {
        hasError = true
        console.error(`Failed to export ${format}:`, error)
        pushToast(
          React.createElement(
            Message,
            { type: 'error' },
            `Failed to download: ${(error as Error).message}`,
          ),
          { placement: 'topCenter', duration: 5000 },
        )
      }

      overallProgress = i + 1

      // Set progress to completed state for this format
      if (totalFormats > 1) {
        const newProgress = Math.round((overallProgress / totalFormats) * 100)
        setTimeout(() => setProgress(newProgress), 0)
      }
    }

    // Reset progress and animate status
    setTimeout(() => setProgress(0), 0)
    state.animateExportStatus(hasError)
  },

  fetchReportFromServer: async (
    endpoint: string,
    reportId: string,
    pushToast?: any,
  ) => {
    const state = get()

    set((stateObj) => {
      stateObj.fetchingReportIsLoading = true
    })

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}${endpoint}/${reportId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      const res = await response.json()
      const reportData = res.data

      // Initialize report data using base slice
      state.initialize(reportData)
    } catch (error) {
      console.error(error)
      if (pushToast) {
        pushToast(
          React.createElement(
            Message,
            { type: 'error' },
            `Failed to load report: ${(error as Error).message}`,
          ),
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } finally {
      set((stateObj) => {
        stateObj.fetchingReportIsLoading = false
      })
    }
  },

  handleSaveReportTemplate: async (params: {
    savingStateKey?: string
    updateFunction: (reportId: number) => Promise<boolean>
    saveNewFunction: () => Promise<boolean>
    pushToast: any
    setSavingState: (key: string | null, value: boolean) => void
  }) => {
    const {
      savingStateKey,
      updateFunction,
      saveNewFunction,
      pushToast,
      setSavingState,
    } = params
    const state = get()

    setSavingState(savingStateKey || null, true)

    try {
      let success = false
      const reportId = state.freshReportId || state.routedReportId

      if (reportId) {
        success = await updateFunction(reportId)
        if (success) {
          pushToast(
            React.createElement(
              Message,
              { type: 'success' },
              'Report updated successfully',
            ),
            { placement: 'topCenter', duration: 3000 },
          )
        }
      } else {
        success = await saveNewFunction()
        if (success) {
          pushToast(
            React.createElement(
              Message,
              { type: 'success' },
              'Report saved successfully',
            ),
            { placement: 'topCenter', duration: 3000 },
          )
        }
      }

      if (!success) {
        pushToast(
          React.createElement(
            Message,
            { type: 'error' },
            'Failed to save report',
          ),
          { placement: 'topCenter', duration: 5000 },
        )
      }
    } catch (error) {
      console.error('🔍 [DEBUG] Error saving report:', error)
      pushToast(
        React.createElement(
          Message,
          { type: 'error' },
          `Error: ${(error as Error).message || 'Failed to save report'}`,
        ),
        { placement: 'topCenter', duration: 5000 },
      )
    } finally {
      setSavingState(savingStateKey || null, false)
    }
  },

  // Getter functions for AI-related properties
  getTitle: () => {
    const state = get()
    return state.anyReport?.name || null
  },

  getAIPrompts: () => {
    const state = get()
    return state.anyReport?.ai_prompts
  },

  getAIPlus: () => {
    const state = get()
    return state.anyReport?.project_solution?.features?.ai || false
  },

  getAISettings: () => {
    const state = get()
    return state.anyReport?.project_solution?.ai_settings
  },

  getReportDocument: () => {
    const state = get()
    return state.anyReport?.executive_summary
  },

  getUserName: () => {
    const state = get()
    return state.userName || 'Unknown User'
  },
})

// ---- Store-aware hooks that accept a store parameter ----
// Type for a store that implements BaseSlice (could be impact, scenario, or other derived stores)
type BaseStoreHook<T extends BaseSlice> = (selector: (state: T) => any) => any

// Hook to watch for changes in ai_plus_description for a specific plot
export const useAIPlusDescription = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
  plotGroupId: number,
  plotPackageId: number,
) => {
  return store((state: T) => {
    if (!state.anyReport?.executive_summary?.plots) return null

    const plotGroup = state.anyReport.executive_summary.plots.find(
      (group: any) => group.id === plotGroupId,
    )
    if (!plotGroup?.plots) return null

    const plotPackage = plotGroup.plots.find(
      (plot: any) => plot.id === plotPackageId,
    )
    return plotPackage?.ai_plus_description || null
  })
}

// Hook to watch for changes in ai_description for a specific plot
export const useAIDescription = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
  plotGroupId: number,
  plotPackageId: number,
) => {
  return store((state: T) => {
    if (!state.anyReport?.executive_summary?.plots) return null

    const plotGroup = state.anyReport.executive_summary.plots.find(
      (group: any) => group.id === plotGroupId,
    )
    if (!plotGroup?.plots) return null

    const plotPackage = plotGroup.plots.find(
      (plot: any) => plot.id === plotPackageId,
    )
    return plotPackage?.ai_description || null
  })
}

// Hook to watch for changes in conclusion
export const useConclusion = <T extends BaseSlice>(store: BaseStoreHook<T>) => {
  return store((state: T) => {
    return state.anyReport?.executive_summary?.conclusion || null
  })
}

// Hook to watch for changes in conclusion_plus
export const useConclusionPlus = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
) => {
  return store((state: T) => {
    return state.anyReport?.executive_summary?.conclusion_plus || null
  })
}

// Hook to watch for changes in key_insights
export const useKeyInsights = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
) => {
  return store((state: T) => {
    return state.anyReport?.executive_summary?.key_insights || null
  })
}

// Hook to watch for changes in key_insights_plus
export const useKeyInsightsPlus = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
) => {
  return store((state: T) => {
    return state.anyReport?.executive_summary?.key_insights_plus || null
  })
}

// Hook for change tracking
export const useHasUnsavedChanges = <T extends BaseSlice>(
  store: BaseStoreHook<T>,
) => {
  return store((state: T) => state.hasUnsavedChanges)
}
